// src/components/SiteConditions.js
// Consultation des conditions d'un site (fiche/bottom-sheet) : pastille "dernières
// conditions" + heatmap saisonnière (mois × semaines-du-mois) avec slider de profondeur.
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchWithAuth } from "../utils/api";
import "./SiteConditions.css";

const DEPTH_BANDS = ["0-10", "10-20", "20-30", "30-40", "40+"];
const VIS_COLORS = { 1: "#8a7a5c", 2: "#6f9e86", 3: "#3fa7b8", 4: "#1c9fd6", 5: "#0077b6" };
const LEVEL_RANGES = { 1: "<1m", 2: "1-3m", 3: "3-6m", 4: "6-10m", 5: ">10m" };

// Médiane d'un tableau de niveaux (échelle ordinale → médiane plus juste que la moyenne)
const median = (arr) => {
    if (!arr.length) return 0;
    const s = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

const monthIndex = (dateStr) => parseInt((dateStr || "").slice(5, 7), 10) - 1;
const weekOfMonth = (dateStr) => {
    const day = parseInt((dateStr || "").slice(8, 10), 10) || 1;
    return Math.min(4, Math.ceil(day / 7)); // 1..4 (la S4 couvre les jours 22 à fin de mois)
};

export default function SiteConditions({ apiUrl, siteId }) {
    const { t, i18n } = useTranslation();
    const [reports, setReports] = useState(null); // null = chargement
    const [bandIdx, setBandIdx] = useState(1); // défaut 10-20m
    const [cell, setCell] = useState(null); // { m, w } sélectionné

    useEffect(() => {
        let cancelled = false;
        setReports(null);
        setCell(null);
        fetchWithAuth(`${apiUrl}/site-conditions/site/${siteId}`)
            .then((r) => (r.ok ? r.json() : []))
            .then((d) => { if (!cancelled) setReports(Array.isArray(d) ? d : []); })
            .catch(() => { if (!cancelled) setReports([]); });
        return () => { cancelled = true; };
    }, [apiUrl, siteId]);

    const band = DEPTH_BANDS[bandIdx];

    // Repère de la semaine actuelle (pour la surligner dans la matrice)
    const nowRef = new Date();
    const curMonth = nowRef.getMonth();
    const curWeek = Math.min(4, Math.ceil(nowRef.getDate() / 7)) - 1;

    // Libellés de mois courts localisés
    const monthLabels = useMemo(
        () => Array.from({ length: 12 }, (_, m) => new Date(2000, m, 1).toLocaleDateString(i18n.language, { month: "short" })),
        [i18n.language]
    );

    // Matrice [mois][semaine] = tableau des niveaux, pour la profondeur choisie
    const matrix = useMemo(() => {
        const grid = Array.from({ length: 12 }, () => Array.from({ length: 4 }, () => []));
        for (const r of reports || []) {
            const lvl = r.visibility && r.visibility[band];
            if (!lvl) continue;
            const m = monthIndex(r.observed_date);
            const w = weekOfMonth(r.observed_date) - 1;
            if (m < 0 || m > 11 || w < 0 || w > 4) continue;
            grid[m][w].push(lvl);
        }
        return grid;
    }, [reports, band]);

    // Rapports d'une case (pour le détail au clic)
    const cellReports = useMemo(() => {
        if (!cell) return [];
        return (reports || []).filter(
            (r) => monthIndex(r.observed_date) === cell.m && weekOfMonth(r.observed_date) - 1 === cell.w
        );
    }, [cell, reports]);

    if (reports === null) {
        return <div className="sc-loading">{t("common.loading")}</div>;
    }

    const latest = reports[0]; // trié par date desc côté backend
    const hasData = reports.length > 0;

    // Fraîcheur de la pastille
    let freshness = null;
    if (latest) {
        const d = new Date(latest.observed_date + "T00:00:00");
        const now = new Date();
        const days = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()) - d) / 86400000);
        freshness = { days, recent: days <= 14 };
    }

    const formatDate = (dateStr) =>
        new Date(dateStr + "T00:00:00").toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric" });

    return (
        <div className="sc">
            <h4 className="sc-title">{t("conditions.latestTitle")}</h4>

            {!hasData ? (
                <div className="sc-empty">{t("conditions.siteEmpty")}</div>
            ) : (
                <>
                    {/* Pastille dernières conditions (colonne d'eau) */}
                    <div className="sc-latest">
                        <div className="sc-latest-date">
                            <span className={`sc-dot ${freshness.recent ? "recent" : "old"}`} />
                            {formatDate(latest.observed_date)}
                            {!freshness.recent && <span className="sc-old-tag">{t("conditions.oldData")}</span>}
                        </div>
                        <div className="sc-column">
                            {DEPTH_BANDS.map((b, i) => {
                                const lvl = latest.visibility && latest.visibility[b];
                                return (
                                    <div className="sc-col-row" key={b}>
                                        <span
                                            className="sc-col-seg"
                                            style={{
                                                background: lvl ? VIS_COLORS[lvl] : "#eef2f4",
                                                borderTopLeftRadius: i === 0 ? 6 : 0,
                                                borderTopRightRadius: i === 0 ? 6 : 0,
                                                borderBottomLeftRadius: i === DEPTH_BANDS.length - 1 ? 6 : 0,
                                                borderBottomRightRadius: i === DEPTH_BANDS.length - 1 ? 6 : 0,
                                            }}
                                        />
                                        <span className="sc-col-depth">{b === "40+" ? "40+ m" : `${b} m`}</span>
                                        <span className="sc-col-lvl">{lvl ? t(`conditions.levels.${lvl}`) : "—"}</span>
                                    </div>
                                );
                            })}
                        </div>
                        {latest.water_temp != null && (
                            <div className="sc-col-temp">{t("conditions.tempShort")} : {latest.water_temp} °C</div>
                        )}
                    </div>
                </>
            )}

            {/* Heatmap saisonnière (toujours affichée, grise si vide) */}
            <div className="sc-heatmap-head">
                <h4 className="sc-title" style={{ margin: 0 }}>{t("conditions.seasonTitle")}</h4>
            </div>

            {/* Slider profondeur */}
            <div className="sc-depth">
                <span className="sc-depth-label">{t("conditions.depth")}</span>
                <input
                    type="range"
                    min={0}
                    max={4}
                    step={1}
                    value={bandIdx}
                    onChange={(e) => { setBandIdx(Number(e.target.value)); setCell(null); }}
                    className="sc-slider"
                />
                <span className="sc-depth-value">{band === "40+" ? "40+ m" : `${band} m`}</span>
            </div>

            {/* Grille mois × semaines-du-mois */}
            <div className="sc-grid" role="img" aria-label={t("conditions.seasonTitle")}>
                <div className="sc-grid-row sc-grid-headrow">
                    <span className="sc-grid-monthlabel" />
                    {[1, 2, 3, 4].map((w) => (
                        <span key={w} className="sc-grid-weekhead">S{w}</span>
                    ))}
                </div>
                {matrix.map((row, m) => (
                    <div className="sc-grid-row" key={m}>
                        <span className={`sc-grid-monthlabel${m === curMonth ? " current" : ""}`}>{monthLabels[m]}</span>
                        {row.map((vals, w) => {
                            const n = vals.length;
                            const lvl = n > 0 ? Math.round(median(vals)) : 0;
                            const active = cell && cell.m === m && cell.w === w;
                            const isCurrent = m === curMonth && w === curWeek;
                            return (
                                <button
                                    key={w}
                                    className={`sc-cell${n > 0 ? " has-data" : ""}${active ? " active" : ""}${isCurrent ? " current" : ""}`}
                                    style={{ background: n > 0 ? VIS_COLORS[lvl] : "#eef2f4" }}
                                    onClick={() => n > 0 && setCell(active ? null : { m, w })}
                                    title={n > 0 ? `${monthLabels[m]} S${w + 1} · ${t(`conditions.levels.${lvl}`)} (${n})` : `${monthLabels[m]} S${w + 1}`}
                                    aria-label={`${monthLabels[m]} semaine ${w + 1}`}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* Légende visi */}
            <div className="sc-legend">
                {[1, 2, 3, 4, 5].map((lvl) => (
                    <span key={lvl} className="sc-legend-item">
                        <span className="sc-legend-dot" style={{ background: VIS_COLORS[lvl] }} />
                        {LEVEL_RANGES[lvl]}
                    </span>
                ))}
                <span className="sc-legend-item">
                    <span className="sc-legend-dot" style={{ background: "#eef2f4" }} />
                    {t("conditions.noDataShort")}
                </span>
            </div>

            {/* Détail d'une case */}
            {cell && cellReports.length > 0 && (
                <div className="sc-cell-detail">
                    <div className="sc-cell-detail-title">
                        {monthLabels[cell.m]} · S{cell.w + 1}
                    </div>
                    {cellReports.map((r) => (
                        <div key={r.id} className="sc-report">
                            <div className="sc-report-head">
                                <span>{formatDate(r.observed_date)}</span>
                                <span className="sc-report-by">{r.reporter?.first_name || "?"}</span>
                            </div>
                            <div className="sc-report-bands">
                                {DEPTH_BANDS.filter((b) => r.visibility && r.visibility[b]).map((b) => (
                                    <span key={b} className="sc-latest-band">
                                        <span className="sc-latest-swatch" style={{ background: VIS_COLORS[r.visibility[b]] }} />
                                        {b === "40+" ? "40+" : b}m
                                    </span>
                                ))}
                                {r.water_temp != null && <span className="sc-latest-temp">{r.water_temp}°C</span>}
                            </div>
                            {r.note && <div className="sc-report-note">{r.note}</div>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
